import React from 'react';
import { motion } from 'motion/react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { MarkdownContentRenderer } from '../../../optumus-core/src/components/MarkdownContentRenderer';
import { StreamingText } from './StreamingText';

interface ChatMarkdownMessageProps {
  content: string;
  isStreaming?: boolean;
  onStreamingComplete?: () => void;
}

const STREAMING_DOT_COUNT = 3;

const StreamingDots: React.FC = () => {
  return (
    <StreamingDotsRow>
      {Array.from({ length: STREAMING_DOT_COUNT }).map((_, index) => (
        <StreamingDot
          key={`streaming-dot-${index}`}
          as={motion.div}
          initial={{ opacity: 0.2 }}
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{
            duration: 0.44,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.14,
          }}
        />
      ))}
    </StreamingDotsRow>
  );
};

const ChatMarkdownMessage: React.FC<ChatMarkdownMessageProps> = ({
  content,
  isStreaming,
  onStreamingComplete,
}) => {
  const normalizedContent = content ?? '';

  if (isStreaming && normalizedContent.trim().length === 0) {
    return (
      <BotMessageContent>
        <StreamingDots />
      </BotMessageContent>
    );
  }

  // Always use StreamingText for the typing animation effect
  return (
    <StreamingText
      text={normalizedContent}
      isStreaming={isStreaming ?? false}
      speed={40}
      maxAnimationDuration={15000}
      onComplete={onStreamingComplete}
    >
      {(displayedText, isAnimating, skipAnimation) => {
        return (
          <ClickableMessageWrapper
            onClick={isAnimating ? skipAnimation : undefined}
            style={{ cursor: isAnimating ? 'pointer' : 'default' }}
          >
            <MarkdownContentRenderer
              content={displayedText}
              ContainerComponent={BotMessageContent}
              ParagraphComponent={BotMessageParagraph}
              ListComponent={BotListContainer}
              ListItemComponent={BotListItemRow}
              BulletComponent={BotListBullet}
              ListItemTextComponent={BotListItemText}
              MarkedTextComponent={BotMessageParagraph}
            />
          </ClickableMessageWrapper>
        );
      }}
    </StreamingText>
  );
};

const BotMessageContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$core.spacing.100',
});

const ClickableMessageWrapper = styled('div', {
  display: 'contents',
});

const StreamingDotsRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$core.spacing.100',
  paddingTop: '$core.spacing.50',
  paddingBottom: '$core.spacing.50',
});

const StreamingDot = styled('div', {
  width: 6,
  height: 6,
  borderRadius: 3,
  backgroundColor: '$core.color.neutral.80',
});

const BotMessageParagraph = styled(Text, {
  color: '$core.color.neutral.90',
  fontSize: 16,
  fontFamily: '$web.core.font-family.sf-pro-text-regular !important',
  lineHeight: '1.4',
});

const BotListContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$core.spacing.100',
});

const BotListItemRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
});

const BotListBullet = styled(Text, {
  color: '$core.color.neutral.90',
  fontSize: '$core.font-size.p.80',
  marginRight: '$core.spacing.100',
  lineHeight: '$core.font-size.p.60',
});

const BotListItemText = styled(Text, {
  flex: 1,
  color: '$core.color.neutral.90',
  fontSize: '$core.font-size.p.80',
});

export default ChatMarkdownMessage;
